import SectionMessage from '@atlaskit/section-message';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'model/State';
import { fetchGenericProperty } from 'modules/mac/duck/actions/genericProperty';
import {
  getCurrentMACContactEmail,
  selectDetails,
  selectGenericProperty,
} from 'modules/mac/duck/selectors';
import { messages } from 'modules/mac/messages';
import { RESTRICTED_EMAIL_DOMAINS } from 'modules/utils';
import DetailsRowContent from './DetailsRowContent';
import { extractTLDFrom } from 'modules/siteconfigurator/utils';
import { StyledSectionMessageWrapper, StyledTableData } from './StyledComponents';
import { fetchAccountHistory } from '../../duck/actions/accountId';

export enum ContactTypes {
  TECHNICAL = 'TECHNICAL',
  BILLING = 'BILLING',
}

export type MappedContact = {
  id: string;
  contactId: string;
  name: string;
  email: string;
  main: boolean;
  canBePromoted: boolean;
  inactiveMakePrimary: boolean;
  noMakePrimary: boolean;
  canBeDeleted: boolean;
  smartDomain: string;
};

export const getContacts = (
  accountDetails,
  contactType,
  mainContact,
  userEmail: string,
): MappedContact[] => {
  const contacts = [];

  if (!accountDetails || !accountDetails.contactRelationships) return contacts;
  const relationShips = accountDetails.contactRelationships;

  const currentUserInRelations = relationShips.find(
    (r) => r.contact.contactDetails.email === userEmail,
  );

  return relationShips
    .filter((contact) => contact.relationshipType === contactType)
    .toSorted((a, b) =>
      a.contact.contactDetails.smartDomain.localeCompare(b.contact.contactDetails.smartDomain),
    )
    .map(({ contact, main, id }) => {
      const mainContactDetails = mainContact?.contactDetails;
      const mainContactEmail = mainContactDetails?.email;

      const { contactDetails } = contact;
      const currentContactIsLoggedInUser = contactDetails.email === userEmail;
      const isMainTechnicalContact = userEmail === mainContactEmail;
      const mainSmartDomain = contactDetails.smartDomain === mainContactDetails?.smartDomain;

      const canBePromoted =
        currentContactIsLoggedInUser ||
        (isMainTechnicalContact && !currentContactIsLoggedInUser && mainSmartDomain);

      const inactiveMakePrimary = !currentContactIsLoggedInUser && !mainSmartDomain;

      const noMakePrimary =
        (contactDetails.email !== mainContactEmail && !currentUserInRelations) ||
        (contactDetails.email === mainContactEmail && !!currentUserInRelations) ||
        RESTRICTED_EMAIL_DOMAINS.includes(extractTLDFrom(contactDetails.email));

      const canBeDeleted = !!currentUserInRelations;

      return {
        id,
        contactId: contactDetails.id,
        name: contactDetails.displayName,
        email: contactDetails.email,
        canBePromoted,
        inactiveMakePrimary,
        noMakePrimary,
        main,
        canBeDeleted,
        smartDomain: contactDetails.smartDomain,
      };
    });
};

const getMainContact = (contacts, contactType) => {
  if (!contacts) return null;

  return contacts.find((cr) => cr.main && cr.relationshipType === contactType)?.contact;
};

const LicensesExpandedRow = ({ productKey, sen, isAccount }) => {
  let genericPropertyFullKey = '';

  const [isOpen, setIsOpen] = useState(false);
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const userEmail = useSelector(getCurrentMACContactEmail) ?? '';
  const detailsData: any = useSelector((state) => selectDetails(state as State, sen));
  const accountDetails = detailsData && detailsData === 'Access denied' ? null : detailsData;
  const genericPropertyObject = useSelector(selectGenericProperty);
  const [canUserSeeAccountDetails, setCanUserSeeAccountDetails] = useState(false);
  const dispatch = useDispatch();

  const mainTechnicalContact = useMemo(
    () => getMainContact(accountDetails?.contactRelationships, ContactTypes.TECHNICAL),
    [],
  );

  const mainBillingContact = useMemo(
    () => getMainContact(accountDetails?.contactRelationships, ContactTypes.BILLING),
    [],
  );

  const technicalContacts = getContacts(
    accountDetails,
    ContactTypes.TECHNICAL,
    mainTechnicalContact,
    userEmail,
  );

  const billingContacts = getContacts(
    accountDetails,
    ContactTypes.BILLING,
    mainBillingContact,
    userEmail,
  );

  let endDate;

  useEffect(() => {
    setCanUserSeeAccountDetails(!(detailsData && detailsData === 'Access denied'));
  }, [detailsData]);

  useEffect(() => {
    if (accountDetails) {
      const productPackage =
        accountDetails.merchandise.productPackage || accountDetails.productPackage;
      if (productPackage?.downloadProduct?.key) {
        genericPropertyFullKey = `related.source.product.${productPackage.downloadProduct.key}`;
      }
      if (genericPropertyFullKey && !genericPropertyObject?.[genericPropertyFullKey]?._result) {
        dispatch(fetchGenericProperty(genericPropertyFullKey));
      }
      if (!accountDetails.hostedType) {
        dispatch(fetchAccountHistory(sen, 0, 10));
      }
    }
  }, [accountDetails]);

  if (accountDetails?.latestLicense) {
    endDate = new Date(accountDetails.latestLicense.expires);
  }

  return (
    <>
      {!canUserSeeAccountDetails ? (
        <StyledTableData $paddingLeft colSpan={5}>
          <StyledSectionMessageWrapper>
            <SectionMessage appearance="error">
              <FormattedMessage {...messages.noPermissionsToSeeDetails} />
            </SectionMessage>
          </StyledSectionMessageWrapper>
        </StyledTableData>
      ) : (
        accountDetails && (
          <DetailsRowContent
            accountDetails={accountDetails}
            canUserSeeAccountDetails={canUserSeeAccountDetails}
            endDate={endDate}
            technicalContacts={technicalContacts}
            billingContacts={billingContacts}
            productKey={productKey}
            isAccount={isAccount}
            isOpen={isOpen}
            openModal={openModal}
            closeModal={closeModal}
          />
        )
      )}
    </>
  );
};

export default LicensesExpandedRow;
